import React, { useEffect } from "react";
import { Download, FileText, SquareX, PencilLine } from "lucide-react";
import {
  downloadResumePdf,
  getUserProfile,
  getUserMessage,
} from "./services/api";
import { useToast } from "./components/ToastContext";

const ProfileScreen = ({
  userData,
  setUserData,
  setResumeData,
  handleSaveProfile,
  handleSaveResumeData,
  handleDownloadAllData,
  deleteAccount,
  handleLogout,
  deleteResume,
  refreshMatchScores,
  setIsRefreshingScores,
  userEmail,
  handleErrors,
  navigateTo,
}) => {
  const toast = useToast();
  const [profileData, setProfileData] = React.useState({});
  const [currentResumeData, setCurrentResumeData] = React.useState({});
  const [numberOfUploads, setNumberOfUploads] = React.useState(0);
  const [selectedResume, setSelectedResume] = React.useState("");
  const [isDownloadingResume, setIsDownloadingResume] = React.useState(false);
  const [confirmEmail, setConfirmEmail] = React.useState("");
  const [showDeleteConfirm, setShowDeleteConfirm] = React.useState(false);

  useEffect(() => {
    // Check if resume exists in database
    const handleGetCurrentResume = async () => {
      const userInfo = await getUserProfile(userEmail, true, true, false, true);
      setProfileData(userInfo.profileData || {});
      setCurrentResumeData(userInfo.resumeData || {});
      setNumberOfUploads(Object.keys(userInfo.resumeData || {}).length || 0);
      setSelectedResume(Object.keys(userInfo.resumeData || {})[0] || "");
    };
    // Check if resume exists in database
    handleGetCurrentResume();
  }, [userData, userEmail]);

  const handleDeleteResume = async () => {
    if (numberOfUploads < 2) {
      toast.info(
        "At least one resume is required. Please upload another before removing this one.",
      );
      return;
    }
    const confirmed = window.confirm(
      `Are you sure you want to delete the ${selectedResume} resume? This action cannot be undone.`,
    );

    if (!confirmed) return;

    try {
      await deleteResume(selectedResume);
      const resumes = Object.fromEntries(
        Object.entries(currentResumeData).filter(
          ([key]) => key !== selectedResume,
        ),
      );
      setUserData({ ...userData, resumeData: resumes });
      setCurrentResumeData(resumes);
      setNumberOfUploads(numberOfUploads - 1);
      setSelectedResume(Object.keys(resumes)[0] || "");
      await handleRefreshMatchScores();
    } catch (error) {
      await handleErrors("Delete resume", error);
      toast.error(getUserMessage(error));
    }
  };

  const handleUpdateProfile = async () => {
    try {
      await handleSaveProfile(profileData);
      await handleSaveResumeData(currentResumeData);

      setUserData({
        ...userData,
        profileData: {
          ...userData.profileData,
          profileData,
        },
        resumeData: {
          ...userData.resumeData,
          currentResumeData,
        },
      });

      navigateTo("home");
    } catch (error) {
      await handleErrors("Update profile", error);
      toast.error(getUserMessage(error));
    }
  };

  const handleEditResume = () => {
    setResumeData(currentResumeData[selectedResume]);
    navigateTo("personalInfo");
  };

  const handleDownloadResume = async () => {
    if (!selectedResume) {
      toast.info("Please select a resume to download.");
      return;
    }

    try {
      setIsDownloadingResume(true);
      const response = await downloadResumePdf(userEmail, selectedResume);
      const pdfDataUri = `data:application/pdf;base64,${response.resume_pdf_base64}`;
      const pdfResponse = await fetch(pdfDataUri);
      const blob = await pdfResponse.blob();
      const url = URL.createObjectURL(blob);
      const safeName =
        (response.resume_name || selectedResume || "resume")
          .replace(/[^a-zA-Z0-9]+/g, "_")
          .replace(/^_+|_+$/g, "") || "resume";

      const link = document.createElement("a");
      link.href = url;
      link.download = `${safeName}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      await handleErrors("Download resume", error);
      toast.error(getUserMessage(error));
    } finally {
      setIsDownloadingResume(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (confirmEmail !== userEmail) {
      toast.error(
        "That email doesn't match. Please type your account email exactly.",
      );
      return;
    }
    try {
      await deleteAccount();
      toast.success("Your account has been deleted.");
      handleLogout();
    } catch (error) {
      await handleErrors("Delete account", error);
      toast.error(
        "We couldn't delete your account. Please contact support or try again.",
      );
    }
  };

  const handleRefreshMatchScores = async () => {
    try {
      setIsRefreshingScores(true);
      await refreshMatchScores();
    } catch (error) {
      console.error("Error refreshing match scores");
      await handleErrors("Unable to refresh jobs", error);
      toast.error(getUserMessage(error));
    } finally {
      setIsRefreshingScores(false);
    }
  };

  return (
    <div className="flex-1 bg-gray-50 p-4">
      <div className="h-full max-w-6xl mx-auto">
        <div className="bg-white rounded-xl shadow-lg p-4">
          <div className="flex items-center justify-between ">
            <h2 className="applicant-theme-text">Profile & Resume</h2>
          </div>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  First Name *
                </label>
                <input
                  type="text"
                  value={profileData.first_name || ""}
                  onChange={(e) => {
                    setProfileData({
                      ...profileData,
                      first_name: e.target.value,
                    });
                  }}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Last Name *
                </label>
                <input
                  type="text"
                  value={profileData.last_name || ""}
                  onChange={(e) => {
                    setProfileData({
                      ...profileData,
                      last_name: e.target.value,
                    });
                  }}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Email *
              </label>
              <input
                type="email"
                value={profileData.email || ""}
                onChange={(e) =>
                  setProfileData({
                    ...profileData,
                    email: e.target.value,
                  })
                }
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>

            <div className="border-t pt-6 items-center mb-2">
              <h3 className="text-lg font-semibold text-gray-900  ">
                Resume Status
              </h3>
              {numberOfUploads > 0 ? (
                <div className="ml-2 items-center justify-center text-center">
                  <p className="text-sm text-gray-600">
                    You have uploaded {numberOfUploads} resumes and can upload{" "}
                    {3 - numberOfUploads} more.
                  </p>
                  <div className="buttons-container">
                    <button
                      className="buttons-red-mini"
                      onClick={() => handleDeleteResume()}
                    >
                      <SquareX className="w-5 h-5" />
                    </button>
                    <select
                      className="dropdown-select"
                      onChange={(e) => setSelectedResume(e.target.value)}
                    >
                      {Object.keys(currentResumeData).map((resumeName) => (
                        <option key={resumeName} value={resumeName}>
                          {resumeName}
                        </option>
                      ))}
                    </select>
                    <button
                      className="buttons-navigation-mini px-2 m-sm"
                      onClick={handleEditResume}
                    >
                      <PencilLine className="min-w-2 min-h-2" />
                    </button>
                    <button
                      className="buttons-navigation-mini px-2 m-sm"
                      onClick={handleDownloadResume}
                      disabled={isDownloadingResume}
                      aria-label="Download selected resume as PDF"
                      title="Download resume PDF"
                    >
                      <Download className="min-w-2 min-h-2" />
                    </button>
                  </div>
                  <div className="bg-indigo-50 border rounded-lg p-4 text-left m-sm">
                    <h4 className="font-medium text-indigo-600 mb-2">
                      Resume Summary
                    </h4>
                    <div className="text-sm text-indigo-600 space-y-2">
                      <p>
                        <strong>Work Experience:</strong>{" "}
                        {currentResumeData[selectedResume]?.work_experience
                          ?.length || 0}{" "}
                        positions
                      </p>
                      <p>
                        <strong>Skills Categories:</strong>{" "}
                        {
                          Object.keys(
                            currentResumeData[selectedResume]?.skills || {},
                          ).length
                        }
                      </p>
                      <p>
                        <strong>Projects:</strong>{" "}
                        {currentResumeData[selectedResume]?.projects?.length ||
                          0}
                      </p>
                      <p>
                        <strong>Education:</strong>{" "}
                        {currentResumeData[selectedResume]?.education?.length ||
                          0}{" "}
                        entries
                      </p>
                      {currentResumeData[selectedResume]?.summary && (
                        <p>
                          <strong>Summary:</strong>{" "}
                          {currentResumeData[selectedResume]?.summary.substring(
                            0,
                            100,
                          )}
                          ...
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 flex items-center justify-between">
                  <div className="flex items-center">
                    <FileText className="w-5 h-5 text-gray-400 mr-2" />
                    <span className="text-gray-600">No resume uploaded</span>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-3 gap-4 items-center">
                <button
                  onClick={() => navigateTo("start")}
                  className="buttons-indigo h-8"
                  disabled={numberOfUploads >= 3}
                >
                  Upload Resume
                </button>
                <button
                  className="buttons-navigation h-8"
                  onClick={handleUpdateProfile}
                >
                  Save Changes
                </button>
                <button
                  className="buttons-indigo h-8"
                  onClick={handleDownloadAllData}
                >
                  Download My Data
                </button>
              </div>
            </div>

            <div className="buttons-container">
              {!showDeleteConfirm ? (
                <button
                  className="buttons-red right"
                  onClick={() => setShowDeleteConfirm(true)}
                >
                  Delete Account
                </button>
              ) : (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 space-y-3">
                  <p className="text-sm font-semibold text-red-800">
                    This will permanently delete your account and all data. This
                    cannot be undone.
                  </p>
                  <p className="text-xs text-red-700">
                    Type your email address to confirm:{" "}
                    <strong>{userEmail}</strong>
                  </p>
                  <input
                    type="email"
                    value={confirmEmail}
                    onChange={(e) => setConfirmEmail(e.target.value)}
                    placeholder="Enter your email"
                    className="w-full px-4 py-3 border border-red-300 rounded-lg focus:ring-2 focus:ring-red-400"
                  />
                  <div className="buttons-container">
                    <button
                      className="buttons-navigation"
                      onClick={() => {
                        setShowDeleteConfirm(false);
                        setConfirmEmail("");
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      className="buttons-red"
                      onClick={handleDeleteAccount}
                      disabled={confirmEmail !== userEmail}
                    >
                      Permanently Delete
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileScreen;
